import * as Api from '@ViewModels';
import { skipToken, useQuery } from '@tanstack/react-query';
import { css } from 'aphrodite';
import * as React from 'react';
import { useImageAttachmentMutation } from '../../../queries/Attachments/useImageAttachmentMutation';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { useGetImageUrl } from '../../components/TestimonialImageEditor/hooks/useGetImageUrl';
import { ChevronLeft } from '../../components/svgs/icons/ChevronLeft';
import { ChevronRight } from '../../components/svgs/icons/ChevronRight';
import { Button } from '../Button';
import { ICropFraming, ImageCropper } from '../ImageCropDialog';
import { isRedrawable } from './redrawableMedia';
import { s } from './styles';
import { CropOptions } from './types';

const CROP_SOURCE_QUERY_KEY = 'attachment/<id>/crop-source';
// Data URLs run to megabytes, so an image paged away from is dropped soon after rather than at the default.
const CROP_SOURCE_GC_TIME_MS = 60_000;

/**
 * Reframes the post's media one picture at a time, handing each crop back as a freshly uploaded
 * attachment. A crop made earlier in the session opens over the picture it was cut from, framed as it
 * was, so cropping again never crops a crop, and it can be undone.
 *
 * The cropper needs a data URL — a cross-origin attachment taints the canvas and `toBlob` then throws —
 * so the picture on screen is resolved through the attachment stream before it can be cropped.
 */
export function CropTab({
	cropSources,
	images,
	impersonationContext,
	initialIndex,
	onCropped,
	onUndoCrop,
}: CropOptions & { impersonationContext?: Api.IImpersonationContext }) {
	// Each copy keeps its position in `images`, since one attachment can appear there more than once.
	const croppable = React.useMemo(
		() => (images ?? []).map((image, index) => ({ image, index })).filter(({ image }) => isRedrawable(image)),
		[images]
	);
	// Starts on the requested image, so opening the tab never downloads the first one on the way there.
	const [page, setPage] = React.useState(() =>
		Math.max(
			croppable.findIndex(({ index }) => index === initialIndex),
			0
		)
	);

	const { getImageUrl } = useGetImageUrl(impersonationContext);
	const { mutateAsync: uploadCroppedImage } = useImageAttachmentMutation();

	React.useEffect(() => {
		const requested = croppable.findIndex(({ index }) => index === initialIndex);
		if (requested >= 0) {
			setPage(requested);
		}
	}, [initialIndex, croppable]);

	const safePage = Math.min(page, Math.max(croppable.length - 1, 0));
	const current = croppable[safePage];
	const source = current ? cropSources?.[current.image.id] : undefined;
	const picture = source?.original ?? current?.image;

	// Keyed on the id rather than the attachment: the media is rebuilt on every edit to the post, and
	// re-resolving the same picture would throw away a crop in progress. The bytes behind an id never
	// change, so nothing refetches it either.
	const { data: sourceUrl, isError: loadFailed } = useQuery({
		gcTime: CROP_SOURCE_GC_TIME_MS,
		queryFn: picture ? () => getImageUrl(picture) : skipToken,
		queryKey: [CROP_SOURCE_QUERY_KEY, impersonationContext?.account?.id, picture?.id],
		retry: 1,
		staleTime: Infinity,
	});

	const step = (delta: number) => setPage((safePage + delta + croppable.length) % croppable.length);

	const handleCropComplete = async (croppedFile: File, framing: ICropFraming) => {
		let attachment: Api.IFileAttachment;
		try {
			[attachment] = await uploadCroppedImage({
				files: [croppedFile],
				impersonationContext,
				resizeForSocialMedia: true,
			});
		} catch {
			// The cropper shows what it is given; a rejected upload result is not an Error and would
			// otherwise read as a failure to crop.
			throw new Error('Could not upload the cropped image. Please try again.');
		}
		// The crop is a new attachment with a new id, so the caller swaps it in for this copy rather than
		// adding it beside it.
		onCropped({ cropped: attachment, framing, index: current.index, original: picture });
	};

	if (!current) {
		return <div className={css(s.cropEmpty)}>Add an image to this post to crop it.</div>;
	}

	return (
		<div className={css(s.cropTab)}>
			{croppable.length > 1 ? (
				<div className={css(s.cropPager)}>
					<Button aria-label='Previous image' onClick={() => step(-1)} size='icon' type='button' variant='ghost'>
						<ChevronLeft />
					</Button>
					<span aria-live='polite' className={css(s.cropCount)}>
						{`${current.image.fileName} — image ${safePage + 1} of ${croppable.length}`}
					</span>
					<Button aria-label='Next image' onClick={() => step(1)} size='icon' type='button' variant='ghost'>
						<ChevronRight />
					</Button>
				</div>
			) : null}
			{loadFailed ? (
				<p className={css(s.cropError)} role='alert'>
					Could not load this image for cropping. Please try again.
				</p>
			) : sourceUrl ? (
				<ImageCropper
					applyLabel='Use this crop'
					extraActions={
						source ? (
							<Button onClick={() => onUndoCrop(current.index)} type='button' variant='secondary'>
								Undo crop
							</Button>
						) : null
					}
					fileName={picture.fileName}
					imageUrl={sourceUrl}
					initialFraming={source?.framing}
					// Each copy, and each picture a copy shows, deserves a fresh crop, even where two share a data URL.
					key={`${current.index}:${picture.id}`}
					onCropComplete={handleCropComplete}
				/>
			) : (
				<div className={css(s.cropLoading)}>
					<LoadingSpinner type='large' />
				</div>
			)}
			{/* The dialog's destination banner names a split post's version, so this note does not. */}
			<p className={css(s.cropNote)}>
				{source ? 'Showing the full picture this crop was cut from.' : 'A crop replaces this image.'}
			</p>
		</div>
	);
}
